import {
  Children,
  forwardRef,
  isValidElement,
  useId,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/shared/utils/utils';

export interface SelectOption {
  label: string;
  value: string;
  disabled?: boolean;
  group?: string;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'onChange'> {
  label?: string;
  error?: string;
  /** Data-driven mode. Omit to use the compositional <SelectItem> children. */
  options?: SelectOption[];
  placeholder?: string;
  onChange?: SelectHTMLAttributes<HTMLSelectElement>['onChange'];
  /** Compositional mode convenience: receives the raw value string. */
  onValueChange?: (value: string) => void;
  children?: ReactNode;
}

// ─── Compositional primitives ────────────────────────────────────────────────
// These are descriptors, not rendered output: <Select> reads them to build the
// native <option> list. They exist so pages written against the shadcn-style
// API render correctly here without being rewritten. Purely additive.

export interface SelectItemProps {
  value: string;
  disabled?: boolean;
  children?: ReactNode;
}

/** Declares one option. Rendered by the parent <Select>, not on its own. */
export function SelectItem(_props: SelectItemProps) {
  return null;
}
SelectItem.displayName = 'SelectItem';

/** Groups <SelectItem>s. Transparent — the parent <Select> reads through it. */
export function SelectContent({ children }: { children?: ReactNode }) {
  return <>{children}</>;
}
SelectContent.displayName = 'SelectContent';

/** No-op in this implementation: the native <select> is its own trigger. */
export function SelectTrigger(_props: { children?: ReactNode; className?: string }) {
  return null;
}
SelectTrigger.displayName = 'SelectTrigger';

/** Carries the placeholder text for the native <select>. */
export function SelectValue(_props: { placeholder?: string }) {
  return null;
}
SelectValue.displayName = 'SelectValue';

type Collected = { options: SelectOption[]; placeholder?: string };

/** Walk the children tree collecting SelectItem options and any placeholder. */
function collect(children: ReactNode, acc: Collected): Collected {
  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;

    if (child.type === SelectItem) {
      const p = child.props as SelectItemProps;
      acc.options.push({
        value: p.value,
        label: typeof p.children === 'string' ? p.children : String(p.value),
        disabled: p.disabled,
      });
      return;
    }

    if (child.type === SelectValue) {
      const p = child.props as { placeholder?: string };
      if (p.placeholder && !acc.placeholder) acc.placeholder = p.placeholder;
      return;
    }

    // SelectContent / SelectTrigger / any wrapper — descend into it.
    const p = child.props as { children?: ReactNode };
    if (p?.children) collect(p.children, acc);
  });
  return acc;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    { className, label, error, options, placeholder, id, onChange, onValueChange, children, ...props },
    ref,
  ) => {
    const generatedId = useId();
    const selectId = id ?? props.name ?? generatedId;

    // Compositional mode: derive the option list from <SelectItem> children.
    let resolved = options;
    let resolvedPlaceholder = placeholder;
    if (!resolved) {
      const found = collect(children, { options: [] });
      resolved = found.options;
      resolvedPlaceholder = placeholder ?? found.placeholder;
    }

    const safeOptions = Array.isArray(resolved) ? resolved.filter(Boolean) : [];

    return (
      <div className="w-full">
        {label && (
          <label htmlFor={selectId} className="mb-1.5 block text-label-md text-on-surface">
            {label}
          </label>
        )}
        <div className="relative">
          <select
            ref={ref}
            id={selectId}
            className={cn(
              'h-10 w-full appearance-none rounded-lg border bg-card px-3 pr-9 text-data-md text-on-surface shadow-xs',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              'disabled:cursor-not-allowed disabled:bg-surface-variant disabled:opacity-60',
              error ? 'border-destructive focus-visible:ring-destructive/40' : 'border-input',
              className,
            )}
            onChange={(e) => {
              onChange?.(e);
              onValueChange?.(e.target.value);
            }}
            {...props}
            aria-invalid={!!error || props['aria-invalid']}
            aria-describedby={[props['aria-describedby'], error ? `${selectId}-error` : undefined].filter(Boolean).join(' ') || undefined}
          >
            {resolvedPlaceholder && (
              <option value="" disabled>
                {resolvedPlaceholder}
              </option>
            )}
            {(() => {
              const hasGroups = safeOptions.some((o) => Boolean(o.group));
              if (!hasGroups) {
                return safeOptions.map((o, idx) => {
                  const val = o.value !== undefined && o.value !== null ? String(o.value) : '';
                  const lbl = o.label !== undefined && o.label !== null ? String(o.label) : val;
                  return (
                    <option key={`${val}_${idx}`} value={val} disabled={o.disabled}>
                      {lbl}
                    </option>
                  );
                });
              }

              const rendered: ReactNode[] = [];
              let currentGroup: string | undefined = undefined;
              let currentGroupOptions: SelectOption[] = [];

              const flushGroup = () => {
                if (currentGroup && currentGroupOptions.length > 0) {
                  rendered.push(
                    <optgroup key={`group_${currentGroup}`} label={currentGroup}>
                      {currentGroupOptions.map((o, idx) => (
                        <option key={`${o.value}_${idx}`} value={String(o.value)} disabled={o.disabled}>
                          {o.label !== undefined && o.label !== null ? String(o.label) : String(o.value)}
                        </option>
                      ))}
                    </optgroup>
                  );
                  currentGroup = undefined;
                  currentGroupOptions = [];
                }
              };

              safeOptions.forEach((o, idx) => {
                if (o.group) {
                  if (currentGroup !== o.group) {
                    flushGroup();
                    currentGroup = o.group;
                  }
                  currentGroupOptions.push(o);
                } else {
                  flushGroup();
                  rendered.push(
                    <option key={`${o.value}_${idx}`} value={String(o.value)} disabled={o.disabled}>
                      {o.label !== undefined && o.label !== null ? String(o.label) : String(o.value)}
                    </option>
                  );
                }
              });
              flushGroup();

              return rendered;
            })()}
          </select>
          <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-on-surface-subtle" />
        </div>
        {error && <p id={`${selectId}-error`} role="alert" className="mt-1 text-body-sm text-destructive">{error}</p>}
      </div>
    );
  },
);
Select.displayName = 'Select';
